import { useLayoutEffect, useState, useMemo } from "react";

import dayjs from "dayjs";
import styledComponents from "styled-components";

import { AreaGraph, LineGraph } from "@similarweb/pro-logic-components";

import { useCategoriesComparison } from "app/pages/industry-analysis/categories-context";
import type { ChartData } from "app/pages/industry-analysis/overview/utils/types";
import { NoData } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import DurationServiceDefault, { BasicDurations } from "app/services/DurationService";

import { useHighLevelMetricsData } from "../../hooks/useHighLevelMetricsData";
import { getSingleMetricConfig } from "../config/config";
import { CHART_COLORS } from "../config/consts";
import { useHighLevelMetricsContext } from "../context";
import { filterChartData } from "../utils/chartUtils";
import { parseServerDataBase, parseServerDataPeriodOverPeriod } from "../utils/dataHandler";
import { createTooltipConfig } from "../utils/tooltipUtils";

const LoaderContainer = styledComponents.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 200px;
`;

export const HighLevelMetricsSingularChart = () => {
    const { categories } = useCategoriesComparison();
    const categoryObject = categories[0]?.category; // always single category in this context
    const hasSegments = CategoryServiceUtils.hasSegments(categoryObject);
    const { queryParams, selectedMetric, timeGranularity, legendItems, isPeriodOverPeriod, setLegendItems } =
        useHighLevelMetricsContext();

    const safeSelectedMetric = selectedMetric || getSingleMetricConfig("monthly-visits");
    const [rawAreaGraphData, setRawAreaGraphData] = useState<ChartData>([]);

    const { data: rawData, isLoading } = useHighLevelMetricsData(
        {
            duration: queryParams.duration,
            country: queryParams.country,
            webSource: queryParams.webSource,
            comparedDuration: queryParams.comparedDuration,
            includeSubdomains: queryParams.includeSubdomains,
        },
        categoryObject,
    );

    // Filter chart data based on visible legend items
    const filteredAreaGraphData = useMemo(() => {
        return filterChartData({
            rawAreaGraphData,
            legendItems,
            isPeriodOverPeriod,
        });
    }, [rawAreaGraphData, legendItems, isPeriodOverPeriod]);

    useLayoutEffect(() => {
        if (isLoading || !rawData) {
            return;
        }

        const contextForParsing = {
            queryParams,
            selectedMetric: safeSelectedMetric,
            timeGranularity,
            legendItems,
            isPeriodOverPeriod,
        };

        const parseServerData = isPeriodOverPeriod ? parseServerDataPeriodOverPeriod : parseServerDataBase;
        const parsedChartData = parseServerData(rawData, contextForParsing);

        // For POP mode, replace generic period names with actual date ranges
        if (isPeriodOverPeriod && parsedChartData && Array.isArray(parsedChartData) && parsedChartData.length > 0) {
            const durationData = DurationServiceDefault.getDurationData(
                queryParams.duration,
                queryParams.comparedDuration,
            );
            const { forWidget } = durationData;
            const [currentPeriod, previousPeriod] = forWidget;

            // Update series names with actual period dates - use index-based mapping for POP
            const updatedChartData = parsedChartData.map((series, index) => {
                if (index === 0) {
                    // First series is previous period
                    return {
                        ...series,
                        name: `${previousPeriod} All traffic`,
                        color: CHART_COLORS.PREVIOUS_PERIOD, // Orange for previous period
                    };
                } else if (index === 1) {
                    // Second series is current period
                    return {
                        ...series,
                        name: `${currentPeriod} All traffic`,
                        color: CHART_COLORS.CURRENT_PERIOD, // Blue for current period
                    };
                }
                return series;
            });

            // Only update legend items if they don't match the new period names (prevent infinite loop)
            const expectedPreviousName = previousPeriod; // Just the period, no "All traffic"
            const expectedCurrentName = currentPeriod; // Just the period, no "All traffic"
            const needsLegendUpdate =
                !legendItems.find((item) => item.name === expectedPreviousName) ||
                !legendItems.find((item) => item.name === expectedCurrentName);

            if (needsLegendUpdate) {
                const updatedLegendItems = [
                    {
                        name: expectedPreviousName,
                        color: CHART_COLORS.PREVIOUS_PERIOD, // Orange for previous period
                        visible: true,
                        metric: "Total",
                    },
                    {
                        name: expectedCurrentName,
                        color: CHART_COLORS.CURRENT_PERIOD, // Blue for current period
                        visible: true,
                        metric: "Total",
                    },
                ];
                setLegendItems(updatedLegendItems);
            }

            setRawAreaGraphData(updatedChartData);
        } else if (parsedChartData && Array.isArray(parsedChartData) && parsedChartData.length > 0) {
            // Check if we only have Total data (no device breakdown)
            const availableDevices = rawData.Graph ? Object.keys(rawData.Graph) : [];
            const hasOnlyTotal = availableDevices.length === 1 && availableDevices[0] === "Total";

            if (hasOnlyTotal) {
                // When we only have Total data, hide legend completely since there's no device filtering
                // Only update if legend isn't already empty (prevent infinite loop)
                if (legendItems.length > 0) {
                    setLegendItems([]);
                }
            }

            setRawAreaGraphData(parsedChartData);
        }
    }, [
        rawData,
        legendItems,
        safeSelectedMetric,
        isLoading,
        timeGranularity,
        isPeriodOverPeriod,
        queryParams,
        setLegendItems,
    ]);

    if (isLoading) {
        return (
            <LoaderContainer>
                <LoadingSpinner />
            </LoaderContainer>
        );
    }

    const hasData =
        filteredAreaGraphData &&
        filteredAreaGraphData.length > 0 &&
        filteredAreaGraphData.some((series) => Array.isArray(series.data) && series.data.length > 0);

    if (!hasData) {
        return (
            <NoData
                paddingTop="80px"
                noDataTitleKey="global.nodata.notavilable"
                noDataSubTitleKey="workspaces.marketing.nodata.subtitle"
            />
        );
    }

    const hasSingleDataPoints = filteredAreaGraphData.some(
        (series) => Array.isArray(series.data) && series.data.length === 1,
    );

    const isLineChart =
        isPeriodOverPeriod ||
        ["visit-duration", "pages-per-visit", "bounce-rate"].includes(selectedMetric.id || "") ||
        hasSegments ||
        hasSingleDataPoints; // Force line chart for single data points to show markers properly
    const ChartComponent = isLineChart ? LineGraph : AreaGraph;

    // Get formatters from config (similar to comparison component)
    const metricConfig = getSingleMetricConfig(
        selectedMetric.id as
            | "monthly-visits"
            | "unique-visitors"
            | "visit-duration"
            | "pages-per-visit"
            | "bounce-rate",
    );
    const yAxisFormatter = metricConfig?.yAxisFormatter || ((value: number) => value.toString());
    const tooltipFormatter = metricConfig?.tooltipFormatter || ((value: number) => value.toString());

    const { duration } = queryParams;
    const is28d = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;

    const chartConfig = {
        chart: {
            type: isLineChart ? ("line" as const) : ("area" as const),
            height: 280,
        },
        plotOptions: {
            area: {
                stacking: isLineChart ? null : ("normal" as const),
            },
        },
        xAxis: {
            type: "datetime" as const,
            labels: {
                formatter: function () {
                    return dayjs.utc(this.value).format(timeGranularity.format);
                },
            },
        },
        yAxis: {
            labels: {
                formatter: function () {
                    return yAxisFormatter(Number(this.value));
                },
            },
        },
        tooltip: createTooltipConfig(
            isPeriodOverPeriod,
            queryParams,
            filteredAreaGraphData,
            safeSelectedMetric,
            tooltipFormatter,
            is28d,
            timeGranularity,
        ),
    };

    return (
        <ChartComponent
            data={filteredAreaGraphData}
            graphConfig={chartConfig}
            shouldPresentLegend={false}
            keepSameLegendsOnDataUpdate={false}
            {...(isPeriodOverPeriod && { withMarkers: true })}
        />
    );
};
